import { useEffect, useState } from 'react'
import { supabase } from '../../../lib/supabase'

interface Props {
  categoryId: number | null
  parentId: number | null
  onClose: () => void
  onSuccess: () => void
}

interface FormData {
  name: string
  description: string
  help_text: string
  show_tematic_data: boolean
  show_species_data: boolean
  id_especie: number | null
}

interface Especie {
  codigo_especie: number
  nombre_cientifico: string
  nombre_comun: string | null
  // Agrega otros campos si es necesario
}

interface Category {
  id: string;
  name: string;
  description: string | null;
  help_text: string | null;
  show_tematic_data: boolean;
  show_species_data: boolean;
  id_especie: any | null;
  parent_id: any | null;
}

interface CategoryRow {
  id: string
  name: string
  description: string | null
  help_text: string | null
  parent_id: any | null
  show_tematic_data: boolean
  show_species_data: boolean
  id_especie: any | null
  created_at: string
  updated_at: string
}

type CategoryInsert = Omit<CategoryRow, 'id' | 'created_at' | 'updated_at'>
type CategoryUpdate = Partial<CategoryInsert>

export function CategoryForm({
  categoryId,
  parentId,
  onClose,
  onSuccess,
}: Props) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    help_text: '',
    show_tematic_data: false,
    show_species_data: false,
    id_especie: null
  })
  const [loading, setLoading] = useState(false)
  const [currentCategoryParentId, setCurrentCategoryParentId] = useState<number | null>(null)
  const [especies, setEspecies] = useState<Especie[]>([])
  const [loadingEspecies, setLoadingEspecies] = useState(false)

  useEffect(() => {
    // Cargar especies solo si es categoría padre
    if (parentId === null || (categoryId && currentCategoryParentId === null)) {
      loadEspecies()
    }
  }, [parentId, categoryId, currentCategoryParentId])

  useEffect(() => {
    if (categoryId) {
      supabase
        .from('categories')
        .select('*')
        .eq('id', categoryId)
        .single()
        .then(({ data, error }) => {
          if (error) {
            console.error('Error loading category:', error)
            return
          }
          if (!data) return

          const category = data as Category
          setFormData({
            name: category.name || '',
            description: category.description || '',
            help_text: category.help_text || '',
            show_tematic_data: category.show_tematic_data || false,
            show_species_data: category.show_species_data || false,
            id_especie: category.id_especie || null
          })
          setCurrentCategoryParentId(category.parent_id)
        })
    } else {
      // Resetear para nueva categoría
      setCurrentCategoryParentId(null)
    }
  }, [categoryId])

  const loadEspecies = async () => {
    try {
      setLoadingEspecies(true)
      const { data, error } = await supabase
        .from('especies')
        .select('codigo_especie, nombre_cientifico, nombre_comun')
        .order('nombre_cientifico')
      
      if (error) throw error
      setEspecies(data || [])
    } catch (error) {
      console.error('Error loading especies:', error)
    } finally {
      setLoadingEspecies(false)
    }
  }

  const handleSubmit = async () => {
  if (!formData.name.trim()) {
    alert('El nombre es obligatorio')
    return
  }

  setLoading(true)

  try {
    // Crear payload base con tipos específicos
    const payload: CategoryUpdate = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      help_text: formData.help_text.trim(),
      parent_id: parentId,
    }

    // Solo incluir los campos de checkboxes y especie si es una categoría padre
    const isParentCategory = parentId === null || (categoryId && currentCategoryParentId === null)
    
    if (isParentCategory) {
      payload.show_tematic_data = formData.show_tematic_data
      payload.show_species_data = formData.show_species_data
      
      if (formData.show_species_data) {
        payload.id_especie = formData.id_especie
      } else {
        payload.id_especie = null
      }
    }

    // Cast a any solo en la operación específica
    if (categoryId) {
      const { error } = await (supabase
        .from('categories') as any)
        .update(payload)
        .eq('id', categoryId)
      
      if (error) throw error
    } else {
      const { error } = await (supabase
        .from('categories') as any)
        .insert([payload as CategoryInsert])
      
      if (error) throw error
    }

    onSuccess()
  } catch (error: any) {
    console.error('Error saving category:', error)
    alert(`Error al guardar: ${error.message}`)
  } finally {
    setLoading(false)
  }
}

  const handleSpeciesDataChange = (checked: boolean) => {
    setFormData({
      ...formData,
      show_species_data: checked,
      // Resetear id_especie si se desactiva el checkbox
      id_especie: checked ? formData.id_especie : null
    })
  }

  // Determinar si debemos mostrar los checkboxes (solo para categorías padre)
  const showCheckboxes = parentId === null || (categoryId && currentCategoryParentId === null)
  const showSpeciesSelect = showCheckboxes && formData.show_species_data

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto space-y-6">
        <h2 className="text-2xl font-bold mb-6">
          {categoryId ? 'Editar categoría' : parentId ? 'Nueva subcategoría' : 'Nueva categoría'}
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre *
            </label>
            <input
              className="w-full border border-gray-300 p-2 rounded"
              placeholder="Nombre de la categoría"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              className="w-full border border-gray-300 p-2 rounded"
              placeholder="Descripción de la categoría"
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              rows={3}
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Texto de ayuda
            </label>
            <textarea
              className="w-full border border-gray-300 p-2 rounded"
              placeholder="Texto de ayuda para los usuarios"
              value={formData.help_text}
              onChange={e => setFormData({...formData, help_text: e.target.value})}
              rows={3}
              disabled={loading}
            />
          </div>

          {/* Mostrar checkboxes solo si es una categoría padre */}
          {showCheckboxes && (
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="show_tematic_data"
                  checked={formData.show_tematic_data}
                  onChange={(e) => setFormData({ ...formData, show_tematic_data: e.target.checked })}
                  className="mt-1 w-4 h-4 text-green-600 rounded focus:ring-green-500"
                  disabled={loading}
                />
                <div className="flex-1">
                  <label htmlFor="show_tematic_data" className="font-medium cursor-pointer">
                    Mostrar en sección de fichas temáticas
                  </label>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="show_species_data"
                  checked={formData.show_species_data}
                  onChange={(e) => handleSpeciesDataChange(e.target.checked)}
                  className="mt-1 w-4 h-4 text-green-600 rounded focus:ring-green-500"
                  disabled={loading}
                />
                <div className="flex-1">
                  <label htmlFor="show_species_data" className="font-medium cursor-pointer">
                    Mostrar en sección de fichas de especies
                  </label>
                </div>
              </div>

              {/* Select de especies - solo se muestra si show_species_data está activado */}
              {showSpeciesSelect && (
                <div className="ml-7">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Seleccionar especie *
                  </label>
                  {loadingEspecies ? (
                    <p className="text-sm text-gray-500">Cargando especies...</p>
                  ) : (
                    <select
                      className="w-full border border-gray-300 p-2 rounded"
                      value={formData.id_especie || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        id_especie: e.target.value ? parseInt(e.target.value) : null
                      })}
                      disabled={loading}
                      required
                    >
                      <option value="">Selecciona una especie</option>
                      {especies.map((especie) => (
                        <option key={especie.codigo_especie} value={especie.codigo_especie}>
                          {especie.nombre_cientifico}
                          {especie.nombre_comun ? ` (${especie.nombre_comun})` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {formData.id_especie && !loadingEspecies && (
                    <p className="text-xs text-gray-500 mt-1">
                      Esta categoría se vinculará a la especie seleccionada
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <button 
            onClick={onClose} 
            disabled={loading}
            className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !formData.name.trim() || (formData.show_species_data && !formData.id_especie)}
            className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  )
}